import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import moment from "moment";
import Joi from "joi";
import _ from "lodash";
import Input from "./common/input";
import Select from "./common/select";
import Checkbox from "./common/checkbox";
import DependentTable from "./parameters/dependentTable";
import PrecedentTable from "./parameters/precedentTable";
import LinkedTable from "./parameters/LinkedTable";
import ScreensTable from "./parameters/screensTable";
import SharedReportsTable from "./parameters/sharedReportsTable";
import ParameterSubscribe from "./ParameterSubscribe";
import LinkedModal from "./parameters/LinkedModal";
import Modal from "./parameters/dependencyModal";
import http from "../services/http";
import UserContext from "../context/userContext";

const emptyParameter = {
  CODE: "",
  HEADING1: "",
  HEADING2: "",
  HEADING3: "",
  PTYP_CODE: "",
  SFNC_CODE: "",
  PROCESS_TAG: "",
  IWB_TAG: "",
  DISPLAY_PRECISION: 1,
  PCAT_CODE: "",
  IN_USE_FLAG: false,
  AUDIT_FLAG: false,
  UPPER_ABS_LIMIT: 0,
  UPPER_CHK_LIMIT: 0,
  LOWER_CHK_LIMIT: 0,
  LOWER_ABS_LIMIT: 0,
  METER_ROLLOVER_VALUE: 0,
  COMMENTS: "",
  UNITS: "",
  LABEL: "",
  CONSTANT_VALUE: 0,
  LAST_UPDATED_DTS: moment(),
  LAST_UPDATED_BY: "",
  CCF_ANALYSIS: ""
}

const EditParameter = ({ match }) => {
  const [user] = useContext(UserContext);

  const [errors, setErrors] = useState({});
  const [parameter, setParameter] = useState(emptyParameter);
  const [dependents, setDependents] = useState([]);
  const [precedents, setPrecedents] = useState([]);
  const [linked, setLinked] = useState([]);
  const [screens, setScreens] = useState([]);
  const [personalReportCount, setPersonalReportCount] = useState(0);
  const [sharedReports, setSharedReports] = useState([]);
  const [standardFunctions, setStandardFunctions] = useState([]);
  const [parameterTypes, setParameterTypes] = useState([]);
  const [parameterGroups, setParameterGroups] = useState([]);
  const [ccfAnalysis, setCCFAnalysis] = useState([]);
  const [show, setShow] = useState(false);
  const [dependency, setDependency] = useState({
    ID: "",
    SEQUENCE: "",
    DEPENDENT: "",
  });
  const [modalErrors, setModalErrors] = useState({});

  const [showSubscribe, setShowSubscribe] = useState(false);
  const [subscription, setSubscription] = useState({
    code: "",
    site: "",
    type: "",
    email: false,
    push: false,
  });

  const [showLinked, setShowLinked] = useState(false);
  const [linkedData, setLinkedData] = useState({
    firstCode: "",
    firstLabel: "",
    secondCode: "",
    secondLabel: "",
  });
  const [linkedErrors, setLinkedErrors] = useState({});

  const schema = {
    CODE: Joi.string().required().label("Parameter Code"),
    HEADING1: Joi.string().required().label("Heading 1"),
    HEADING2: Joi.string().allow(null, "").label("Heading 2"),
    HEADING3: Joi.string().allow(null, "").label("Heading 3"),
    PTYP_CODE: Joi.string().required().label("Parameter Type"),
    SFNC_CODE: Joi.string().allow(null, "").label("Standard Function"),
    PROCESS_TAG: Joi.string().allow(null, "").label("Process Tag"),
    IWB_TAG: Joi.string().allow(null, "").label("IWB Tag"),
    DISPLAY_PRECISION: Joi.number().min(0).max(7).required().label("Decimal"),
    PCAT_CODE: Joi.string().allow("", null).label("Parameter Category"),
    IN_USE_FLAG: Joi.boolean().required().label("In Use Flag"),
    AUDIT_FLAG: Joi.boolean().required().label("Audit Flag"),
    UPPER_ABS_LIMIT: Joi.number().allow(null, "").label("Upper Absolute Limit"),
    UPPER_CHK_LIMIT: Joi.number().allow(null, "").label("Upper Check Limit"),
    LOWER_CHK_LIMIT: Joi.number().allow(null, "").label("Lower Check Limit"),
    LOWER_ABS_LIMIT: Joi.number().allow(null, "").label("Lower Absolute Limit"),
    METER_ROLLOVER_VALUE: Joi.number().allow(null, "").label("Rollover"),
    COMMENTS: Joi.string().allow(null, "").label("Comments"),
    UNITS: Joi.string().allow(null, "").label("Units"),
    LABEL: Joi.string().allow(null, "").label("Label"),
    CONSTANT_VALUE: Joi.number().allow(null, "").label("Constant Value"),
    LAST_UPDATED_DTS: Joi.date().required(),
    LAST_UPDATED_BY: Joi.string().allow(null, "").required(),
    CCF_ANALYSIS: Joi.string().allow(null, "").label("CCF Analysis")
  };

  const modalSchema = {
    ID: Joi.number().allow(""),
    SEQUENCE: Joi.number().required().label("Sequence Number"),
    DEPENDENT: Joi.string().required().label("Parameter Code"),
  };

  useEffect(() => {
    async function getFunctions() {
      try {
        const { data } = await http.get("/standard-functions");
        setStandardFunctions([{ CODE: "" }, ...data]);
      }
      catch {
        //ignore error
      }
    }

    async function getTypes() {
      try {
        const { data } = await http.get("/parameter-types");
        setParameterTypes(data);
      }
      catch {
        //ignore error
      }
    }

    async function getGroups() {
      try {
        const { data } = await http.get("/parameter-groups");
        setParameterGroups([{ CODE: "" }, ...data]);
      }
      catch {
        //ignore error
      }
    }

    getFunctions();
    getTypes();
    getGroups();

    document.title = "FTR - Parameters";
    return () => {
      document.title = "FTR";
    };
  }, []);

  async function getCCFAnalysis(parameter) {
    try {
      const { data } = await http.get(`/ccf-analysis/parameter/${parameter.CODE}`);
      setCCFAnalysis([{ CODE: "" }, ...data]);
    }
    catch {
      //ignore error
    }
  }

  useEffect(() => {
    async function getInfo() {
      const code = match.params.code.toUpperCase();

      try {
        const { data: searchParameter } = await http.get(`/parameter/${code}`);
        setParameter(mapToView(searchParameter, user.SITE));

        const { data: searchDependents } = await http.get(`/dependencies/dependents/${code.toUpperCase()}`);
        setDependents(searchDependents);

        const { data: searchPrecedents } = await http.get(`/dependencies/precedents/${code.toUpperCase()}`);
        setPrecedents(searchPrecedents);

        const { data: linked } = await http.get(`/parameter-links/${code.toUpperCase()}`);
        setLinked(linked);

        const { data: screens } = await http.get(`/parameter/${code}/data-screens`);
        setScreens(screens);

        const { data: personalReportCount } = await http.get(`/parameter/${code}/personal-reports`);
        setPersonalReportCount(personalReportCount.COUNT);

        const { data: sharedReports } = await http.get(`/parameter/${code}/shared-reports`);
        setSharedReports(sharedReports);

        if (searchParameter && searchParameter.CODE) getCCFAnalysis(searchParameter);      }
      catch {
        //ignore error
      }
    }
    
    if (match.params.code === "Edit") return;
    if (user) {
      getInfo();
    }
  }, [match.params.code, user]);

  function mapToView(parameter, site) {
    return {
      CODE: parameter.CODE,
      HEADING1: parameter.HEADING1 ? parameter.HEADING1 : "",
      HEADING2: parameter.HEADING2 ? parameter.HEADING2 : "",
      HEADING3: parameter.HEADING3 ? parameter.HEADING3 : "",
      PTYP_CODE: parameter.PTYP_CODE,
      SFNC_CODE: parameter.SFNC_CODE ? parameter.SFNC_CODE : "",
      PROCESS_TAG: parameter.PROCESS_TAG ? parameter.PROCESS_TAG : "",
      IWB_TAG: parameter.IWB_TAG ? parameter.IWB_TAG : "",
      DISPLAY_PRECISION: site === "CO" ? 0 : parameter.DISPLAY_PRECISION,
      PCAT_CODE: parameter.PCAT_CODE ? parameter.PCAT_CODE : "",
      IN_USE_FLAG: parameter.IN_USE_FLAG,
      AUDIT_FLAG: parameter.AUDIT_FLAG,
      UPPER_ABS_LIMIT:
        parameter.UPPER_ABS_LIMIT !== null ? parameter.UPPER_ABS_LIMIT : "",
      UPPER_CHK_LIMIT:
        parameter.UPPER_CHK_LIMIT !== null ? parameter.UPPER_CHK_LIMIT : "",
      LOWER_CHK_LIMIT:
        parameter.LOWER_CHK_LIMIT !== null ? parameter.LOWER_CHK_LIMIT : "",
      LOWER_ABS_LIMIT:
        parameter.LOWER_ABS_LIMIT !== null ? parameter.LOWER_ABS_LIMIT : "",
      METER_ROLLOVER_VALUE: parameter.METER_ROLLOVER_VALUE
        ? parameter.METER_ROLLOVER_VALUE
        : "",
      COMMENTS: parameter.COMMENTS ? parameter.COMMENTS : "",
      UNITS: parameter.UNITS ? parameter.UNITS : "",
      LABEL: parameter.LABEL ? parameter.LABEL : "",
      CONSTANT_VALUE:
        parameter.CONSTANT_VALUE !== null ? parameter.CONSTANT_VALUE : "",
      LAST_UPDATED_BY: parameter.LAST_UPDATED_BY,
      LAST_UPDATED_DTS: parameter.LAST_UPDATED_DTS,
      CCF_ANALYSIS: parameter.CCF_ANALYSIS ? parameter.CCF_ANALYSIS : undefined
    };
  }

  function validateProperty({ name, value }, schema) {
    const obj = { [name]: value };
    const propSchema = Joi.object({ [name]: schema[name] });
    const { error } = propSchema.validate(obj);
    return error ? error.details[0].message : null;
  }

  function validate(body, schema) {
    const options = { abortEarly: false };
    const { error } = Joi.object(schema).validate(body, options);
    if (!error) return null;

    const errors = {};
    for (let item of error.details) errors[item.path[0]] = item.message;
    return errors;
  }

  function handleChange({ currentTarget: input }) {
    const newErrors = { ...errors };
    const errorMessage = validateProperty(input, schema);
    if (errorMessage) newErrors[input.name] = errorMessage;
    else delete newErrors[input.name];
    setErrors(newErrors);

    const newParameter = { ...parameter };
    const value = input.type === "checkbox" ? input.checked : input.value;
    newParameter[input.name] = value;
    if (input.name === "PTYP_CODE" && input.value !== "SIM")
      newParameter.SFNC_CODE = "";
    if (input.name === "PTYP_CODE" && input.value !== "CON")
      newParameter.CONSTANT_VALUE = "";
    setParameter(newParameter);
  }

  async function handleUpdate(e) {
    e.preventDefault();

    const newErrors = validate(parameter, schema);
    setErrors(newErrors || {});
    if (newErrors) return;
    
    const putParameter = { ...parameter };
    delete putParameter.CODE;
    delete putParameter.LAST_UPDATED_BY;
    delete putParameter.LAST_UPDATED_DTS;

    try {
      const { data } = await http.put(`/parameter/${parameter.CODE}`, putParameter);
      toast.success(`Parameter ${parameter.CODE} Updated`);
      // toast.success(`Parameter ${parameter.CODE} Updated`, {
      //   onClose: () => window.location = '/parameters'
      // });
      setParameter(mapToView(data, user.SITE));
    } catch (ex) {
      if (ex.response && ex.response.status < 500) {
        toast.error(ex.response.data);
      }
    }
  }

  function handleEdit(p) {
    const newDependency = { ...p };
    delete newDependency.NAME;
    delete newDependency.TYPE;
    delete newDependency.LAST_UPDATED_DTS;
    delete newDependency.LAST_UPDATED_BY;
    setDependency(newDependency);
    setShow(true);
  }

  async function handleRemove(parm) {
    const list = [...dependents];
    const newDependents = list.filter((l) => l.ID !== parm.ID);
    setDependents(newDependents);

    try {
      await http.delete(`/dependencies/${parm.ID}`);
      toast.success(`Parameter ${parm.CODE} removed`);
    } catch (ex) {
      setDependents(list);
      if (ex.response) {
        toast.error("Parameter not deleted");
      }
    }
  }

  function handleModalChange({ currentTarget: input }) {
    const newModalErrors = { ...modalErrors };
    const errorMessage = validateProperty(input, modalSchema);
    if (errorMessage) newModalErrors[input.name] = errorMessage;
    else delete newModalErrors[input.name];
    setModalErrors(newModalErrors);

    const newDependency = { ...dependency };
    newDependency[input.name] = input.value;
    setDependency(newDependency);
  }

  async function handleModalSubmit(e) {
    e.preventDefault();

    const newErrors = validate(dependency, modalSchema);
    setModalErrors(newErrors || {});
    if (newErrors) return;

    if (dependency.ID === "") {
      // POST
      const postDependency = { ...dependency };
      postDependency.DEPENDENT = postDependency.DEPENDENT.toUpperCase();
      // setDependency(postDependency);
      delete postDependency.ID;
      try {
        const { data } = await http.post(`/dependencies/${parameter.CODE}`, postDependency);
        toast.success("Dependency Added");

        const newDependents = [...dependents];
        newDependents.push(data);
        setDependents(newDependents);

        setShow(false);
        setDependency({ ID: "", SEQUENCE: "", DEPENDENT: "" });
      } catch (ex) {
        if (ex.response) {
          const { data } = ex.response;
          toast.error(data);
        }
      }
    } else {
      // PUT
      const putDepedency = { ...dependency };
      putDepedency.DEPENDENT = putDepedency.DEPENDENT.toUpperCase();
      const putId = putDepedency.ID;
      delete putDepedency.ID;

      try {
        const { data } = await http.put(`/dependencies/${putId}`, putDepedency);
        toast.success("Dependency Updated");

        const list = [...dependents];
        const newDependents = list.filter((d) => d.ID !== putId);
        newDependents.push(data);
        const ordered = _.orderBy(newDependents, "SEQUENCE", "asc");
        setDependents(ordered);

        setShow(false);
        setDependency({ ID: "", SEQUENCE: "", DEPENDENT: "" });
      } catch (ex) {
        if (ex.response) {
          const { data } = ex.response;
          toast.error(data);
        }
      }
    }
  }

  const handleShowSubscribe = () => {
    setSubscription({
      code: match.params.code,
      site: user.SITE,
      type: "",
      email: false,
      push: false,
    });
    setShowSubscribe(true);
  };

  const handleSubscriptionChange = ({ currentTarget: input }) => {
    const newData = { ...subscription };
    const value = input.type === "checkbox" ? input.checked : input.value;
    newData[input.name] = value;
    setSubscription(newData);
  };

  const handleShowLinked = () => {
    setLinkedData({
      ...linkedData,
      firstCode: match.params.code,
    });
    setShowLinked(true);
  };

  const handleLinkedChange = ({ currentTarget: input }) => {
    const newData = { ...linkedData };
    const value = input.type === "checkbox" ? input.checked : input.value;
    newData[input.name] = value;
    setLinkedData(newData);
  };

  const handleLinkedSubmit = async (e) => {
    e.preventDefault();

    try {
      const { data } = await http.post(`/parameter-links/`, linkedData);
      toast.success("Link Created");
      setLinked([...linked, data]);
      setShowLinked(false);
      setLinkedData({ firstCode: "" });
    } catch (error) {
      console.log(error);
      toast.error("Failed");
      if (error.response) {
        setLinkedErrors(error.response.data);
      }
    }
  };

  const handleLinkedDelete = async (link) => {
    // Remove List
    const oldLinked = [...linked];
    setLinked(linked.filter((l) => l.ID !== link.ID));
    try {
      await http.delete(`/parameter-links/${link.ID}`);
      toast.success("Link Removed");
    } catch (error) {
      console.log(error);
      toast.error("Failed");
      setLinked(oldLinked);
    }
  };

  const updated = `Last Updated ${moment(parameter.LAST_UPDATED_DTS).calendar()} by ${parameter.LAST_UPDATED_BY}`;

  return (
    <div className="col mt-4">
      <div className="container">
        <div className="row d-flex flex-row-reverse">
          {parameter.PTYP_CODE === "REA" ? (
            <button
              className="btn btn-sm btn-outline-primary position-absolute"
              onClick={handleShowSubscribe}
            >
              Subscribe
            </button>
          ) : null}
        </div>
        <form>
          <h1 className="display-4 text-center text-success">
            {parameter.CODE === "" ? "---" : parameter.CODE}
          </h1>
          <p className="text-center text-success">
            {parameter.CODE === "" ? "" : updated}
          </p>
          <div className="form-row">
            <div className="col-md">
              <Input
                name="HEADING1"
                label="Heading 1"
                placeholder="Heading"
                value={parameter.HEADING1}
                error={errors.HEADING1}
                onChange={handleChange}
              />
              <Input
                name="HEADING2"
                label="Heading 2"
                placeholder="Heading"
                value={parameter.HEADING2}
                error={errors.HEADING2}
                onChange={handleChange}
              />
              <Input
                name="HEADING3"
                label="Heading 3"
                placeholder="Heading"
                value={parameter.HEADING3}
                error={errors.HEADING3}
                onChange={handleChange}
              />
              <div className="row">
                <div className="col">
                  <Input
                    name="UNITS"
                    label="Units"
                    placeholder="e.g. kg, ppm"
                    value={parameter.UNITS}
                    onChange={handleChange}
                    error={errors.UNITS}
                  />
                </div>
                <div className="col">
                  <Input
                    name="LABEL"
                    label="Short Label"
                    placeholder="Label"
                    value={parameter.LABEL}
                    onChange={handleChange}
                    error={errors.LABEL}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-6">
                  <Input
                    name="UPPER_ABS_LIMIT"
                    label="Upper Absolute Limit"
                    placeholder="Limit"
                    value={parameter.UPPER_ABS_LIMIT}
                    error={errors.UPPER_ABS_LIMIT}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-6">
                  <Input
                    name="UPPER_CHK_LIMIT"
                    label="Upper Check Limit"
                    placeholder="Limit"
                    value={parameter.UPPER_CHK_LIMIT}
                    error={errors.UPPER_CHK_LIMIT}
                    onChange={handleChange}
                    disabled={user.SITE === "CO"}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-6">
                  <Input
                    name="LOWER_ABS_LIMIT"
                    label="Lower Absolute Limit"
                    placeholder="Limit"
                    value={parameter.LOWER_ABS_LIMIT}
                    error={errors.LOWER_ABS_LIMIT}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-6">
                  <Input
                    name="LOWER_CHK_LIMIT"
                    label="Lower Check Limit"
                    placeholder="Limit"
                    value={parameter.LOWER_CHK_LIMIT}
                    error={errors.LOWER_CHK_LIMIT}
                    onChange={handleChange}
                    disabled={user.SITE === "CO"}
                  />
                </div>
              </div>
              <div className="row mb-5">
                <div className="col-6">
                  <Checkbox
                    name="IN_USE_FLAG"
                    label="In Use"
                    checked={parameter.IN_USE_FLAG}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-6">
                  <Checkbox
                    name="AUDIT_FLAG"
                    label="Audit"
                    checked={parameter.AUDIT_FLAG}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
            <div className="col-md">
              <div className="row">
                <div className="col-6">
                  <Select
                    name="PTYP_CODE"
                    label="Parameter Type"
                    placeholder="Type"
                    value={parameter.PTYP_CODE}
                    options={parameterTypes}
                    onChange={handleChange}
                    listKey="CODE"
                    listName="DESCRIPTION"
                    error={errors.PTYP_CODE}
                  />
                </div>
                <div className="col-6">
                  <Select
                    name="SFNC_CODE"
                    label="Calculation"
                    placeholder="Function"
                    value={parameter.SFNC_CODE}
                    options={standardFunctions}
                    onChange={handleChange}
                    listKey="CODE"
                    listName="DESCRIPTION"
                    error={errors.SFNC_CODE}
                    disabled={parameter.PTYP_CODE !== "SIM"}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-6">
                  <Input
                    name="PROCESS_TAG"
                    label="Process Tag"
                    placeholder="Tagname"
                    value={parameter.PROCESS_TAG}
                    error={errors.PROCESS_TAG}
                    onChange={handleChange}
                    disabled={parameter.PTYP_CODE !== "REA"}
                  />
                </div>
                <div className="col-6">
                  <Select
                    name="PCAT_CODE"
                    label="Parameter Category"
                    placeholder="Category"
                    value={parameter.PCAT_CODE}
                    options={parameterGroups}
                    onChange={handleChange}
                    error={errors.PCAT_CODE}
                    listKey="CODE"
                    listName="DESCRIPTION"
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-6">
                  <Input
                    name="DISPLAY_PRECISION"
                    label="Decimal Places"
                    placeholder="Precision"
                    value={parameter.DISPLAY_PRECISION}
                    error={errors.DISPLAY_PRECISION}
                    onChange={handleChange}
                    disabled={user.SITE === "CO"}
                  />
                </div>
                <div className="col-6">
                  <Input
                    name="CONSTANT_VALUE"
                    label="Constant Value"
                    placeholder="Number"
                    value={parameter.CONSTANT_VALUE}
                    error={errors.CONSTANT_VALUE}
                    onChange={handleChange}
                    disabled={
                      user.SITE === "CO" || parameter.PTYP_CODE !== "CON"
                    }
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <Input
                    name="METER_ROLLOVER_VALUE"
                    label="Meter Rollover"
                    placeholder="Value"
                    value={parameter.METER_ROLLOVER_VALUE}
                    error={errors.METER_ROLLOVER_VALUE}
                    onChange={handleChange}
                    disabled={
                      user.SITE === "CO" || parameter.PTYP_CODE !== "REA"
                    }
                  />
                </div>
                <div className="col">
                  <Input
                    name="IWB_TAG"
                    label="IWB Tag"
                    placeholder="Tagname"
                    value={parameter.IWB_TAG}
                    error={errors.IWB_TAG}
                    onChange={handleChange}
                    disabled={parameter.PTYP_CODE !== "REA"}
                  />
                </div>
              </div>
              <div className = "row">
                <div className = "col">
                  <Input
                  name="COMMENTS"
                  label="Comments"
                  placeholder="Notes"
                  value={parameter.COMMENTS}
                  error={errors.COMMENTS}
                  onChange={handleChange}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <Select
                    name="CCF_ANALYSIS"
                    label="CCF Analysis"
                    placeholder="CCF Analysis"
                    value={parameter.CCF_ANALYSIS}
                    options={ccfAnalysis}
                    onChange={handleChange}
                    error={errors.CCF_ANALYSIS}
                    listKey="code"
                    listName="name"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="form-row">
            <button
              className="btn btn-primary"
              onClick={handleUpdate}
              // disabled={validate(dependency, modalSchema)}
            >
              Update
            </button>
          </div>
        </form>
        <hr />
        <div className="row mt-4">
          <h4>Dependencies</h4>
          {user && parameter.PTYP_CODE === "SIM" && (
            <button
              className="btn btn-primary btn-sm ml-auto mr-5"
              onClick={() => setShow(true)}
            >
              Add
            </button>
          )}
          <DependentTable
            dependents={dependents}
            activeParameter={parameter}
            user={user}
            editClick={handleEdit}
            removeClick={handleRemove}
          />
        </div>
        <div className="row">
          <h4>Used By</h4>
          <PrecedentTable precendents={precedents} />
        </div>
        <div className="row">
          <h4>Linked Codes</h4>
          <button
            className="btn btn-primary btn-sm ml-auto mr-5"
            onClick={handleShowLinked}
          >
            Add
          </button>
          <LinkedTable parameters={linked} handleDelete={handleLinkedDelete} />
        </div>
        <hr className="bg-success" />
        <div className="row">
          <h4>Data Entry Screens</h4>
          <ScreensTable data={screens} />
        </div>
        <div className="row">
          <h4>Personal Reports - {personalReportCount}</h4>
        </div>
        <div className="row">
          <h4>Shared Reports</h4>
          <SharedReportsTable data={sharedReports} />
        </div>
      </div>
      <LinkedModal
        show={showLinked}
        data={linkedData}
        errors={linkedErrors}
        handleChange={handleLinkedChange}
        handleSubmit={handleLinkedSubmit}
        handleHide={() => setShowLinked(false)}
      />
      <Modal
        show={show}
        data={dependency}
        errors={modalErrors}
        handleChange={handleModalChange}
        handleSubmit={handleModalSubmit}
        handleHide={() => setShow(false)}
      />
      <ParameterSubscribe
        show={showSubscribe}
        handleChange={handleSubscriptionChange}
        handleHide={() => setShowSubscribe(false)}
        data={subscription}
      />
    </div>
  );
};

export default EditParameter;
